/** Форматирование значений для интерфейса (русская локаль). */

export function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Число с разделителями разрядов по-русски: 1 234 567.
 *
 * Понадобится с первых KPI-виджетов; вынесено сюда сразу, чтобы формат
 * не расходился между экранами.
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('ru-RU')
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Дата из канонического представления в привычное российское.
 *
 * Значения в наборе данных хранятся в ISO (`2025-02-11`), а пользователь
 * загружал `11.02.2025` и должен увидеть свой формат. Разбираем строку, а не
 * `new Date()`: ISO-дата без времени трактуется как UTC, и в часовом поясе
 * западнее Гринвича день сдвинулся бы на предыдущий.
 */
export function formatIsoDate(value: string): string {
  const match = ISO_DATE.exec(value)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value
}

function formatIsoDateTime(value: string): string {
  const [datePart = '', timePart] = value.split('T')
  if (!ISO_DATE.test(datePart)) return value
  const time = timePart?.slice(0, 5)
  return time ? `${formatIsoDate(datePart)} ${time}` : formatIsoDate(datePart)
}

/**
 * Число из канонической строки в русском формате: `41803.12` → `41 803,12`.
 *
 * Дробная часть сохраняется как есть: округлять суммы в предпросмотре нельзя —
 * пользователь сверяет их со своим файлом.
 */
export function formatDecimalString(value: string): string {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return value
  const fractionDigits = value.split('.')[1]?.length ?? 0
  return parsed.toLocaleString('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

/**
 * Значение ячейки в том виде, в котором его ожидает увидеть пользователь.
 *
 * В хранилище значения приведены к каноническому виду (ISO-дата, точка как
 * десятичный разделитель), а на экране должны быть в русском формате — иначе
 * предпросмотр не совпадёт с исходным файлом. Тип берём из профиля колонки:
 * по самой строке `12.05` не отличить дату от суммы.
 */
export function formatCellValue(value: unknown, type?: string): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  if (typeof value === 'number') return formatNumber(value)
  if (typeof value !== 'string') return JSON.stringify(value)

  if (type === 'date') return formatIsoDate(value)
  if (type === 'datetime') return formatIsoDateTime(value)
  if (type === 'integer' || type === 'decimal') return formatDecimalString(value)
  if (type === 'boolean') return value === 'true' ? 'да' : value === 'false' ? 'нет' : value
  return value
}

export function formatMoney(value: number, currency = 'RUB'): string {
  return value.toLocaleString('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  })
}

/**
 * Значение показателя в виде, пригодном для дашборда.
 *
 * Числа приходят строками (backend не превращает Decimal в float, чтобы не
 * терять копейки), поэтому разбор — здесь. Крупные суммы сокращаются: «1,8 млн»
 * читается с первого взгляда, а «1 785 963,71» на плитке KPI приходится
 * разбирать по разрядам.
 */
export function formatMetric(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) return String(value)

  const absolute = Math.abs(parsed)
  if (absolute >= 1_000_000_000) return `${trimZeros(parsed / 1_000_000_000)} млрд`
  if (absolute >= 1_000_000) return `${trimZeros(parsed / 1_000_000)} млн`
  if (absolute >= 100_000) return `${trimZeros(parsed / 1_000)} тыс.`

  return parsed.toLocaleString('ru-RU', {
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

function trimZeros(value: number): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })
}

/** Точное значение с разделителями разрядов — для подписи и подсказки. */
export function formatExact(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) return String(value)
  return parsed.toLocaleString('ru-RU', {
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

/** Процент изменения со знаком: `+12,4 %`, `−3,1 %`. */
export function formatPercent(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) return null
  const sign = parsed > 0 ? '+' : parsed < 0 ? '−' : ''
  return `${sign}${Math.abs(parsed).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

/**
 * Подпись точки временной оси по шагу группировки.
 *
 * Ось «01.01.2026, 01.02.2026 …» читается плохо: на графике по месяцам нужен
 * «янв 2026». Шаг известен из ключа колонки результата.
 */
export function formatAxisLabel(label: string, granularity?: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label)
  if (!match) return label

  const [, year = '', month = '', day = ''] = match
  const monthName = MONTHS[Number(month) - 1] ?? month

  switch (granularity) {
    case 'month':
      return `${monthName} ${year}`
    case 'quarter':
      return `${Math.floor((Number(month) - 1) / 3) + 1} кв. ${year}`
    case 'year':
      return year
    case 'week':
      return `${day}.${month}`
    default:
      return `${day}.${month}.${year}`
  }
}

/**
 * Форматирование подписей числовой оси в одной единице измерения.
 *
 * Единица выбирается один раз по наибольшему значению ряда. Если форматировать
 * каждую подпись независимо, ось получается вида «50 000, 100 тыс., 300 тыс.» —
 * читатель сравнивает числа в разных масштабах и делает неверный вывод.
 */
export function makeAxisFormatter(maxAbsolute: number): (value: number) => string {
  const [divisor, suffix] =
    maxAbsolute >= 1_000_000_000
      ? [1_000_000_000, ' млрд']
      : maxAbsolute >= 1_000_000
        ? [1_000_000, ' млн']
        : maxAbsolute >= 10_000
          ? [1_000, ' тыс.']
          : [1, '']

  return (value: number) => {
    // Ноль без единицы: «0 тыс.» выглядит как ошибка вычисления
    if (value === 0) return '0'
    const scaled = (value / divisor).toLocaleString('ru-RU', {
      maximumFractionDigits: divisor === 1 ? 0 : 1,
    })
    return `${scaled}${suffix}`
  }
}

/** Шаг временной оси из ключа колонки результата (`date_month` → `month`). */
export function granularityFromKey(key: string): string | undefined {
  const parts = key.split('_')
  return parts.length > 1 ? parts[parts.length - 1] : undefined
}
